if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector(
          "span:not(.svg-wrapper)",
        );

        if (this.submitButton && document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        const primaryId = formData.get("id");
        const sectionIds = this.cart
          ? this.cart.getSectionsToRender().map((section) => section.id)
          : [];

        // Add-on accessories: any ticked accessory checkbox turns this into a
        // multi-item add — the stove plus each accessory as its own cart line,
        // in one atomic request. Each accessory carries `_voor` line properties
        // so the cart can group it under its parent stove: the title (shown as
        // "Hoort bij:"), the parent variant id (drives cascade removal) and the
        // parent product URL (makes the title a link). With none ticked, the
        // original single-variant FormData path is used unchanged.
        const checkedAccessories = Array.from(
          this.form.querySelectorAll(".accessory-checkbox:checked"),
        );
        if (checkedAccessories.length) {
          const quantity = parseInt(formData.get("quantity"), 10) || 1;
          const parentTitle = this.dataset.productTitle || "";
          const parentUrl = this.dataset.productUrl || "";
          const items = [{ id: primaryId, quantity }];
          checkedAccessories.forEach((checkbox) => {
            items.push({
              id: checkbox.dataset.variantId,
              quantity: 1,
              properties: {
                _voor: parentTitle,
                _voor_id: primaryId,
                _voor_url: parentUrl,
              },
            });
          });
          config.headers["Content-Type"] = "application/json";
          config.body = JSON.stringify({
            items,
            sections: sectionIds,
            sections_url: window.location.pathname,
          });
        } else {
          if (this.cart) {
            formData.append("sections", sectionIds);
            formData.append("sections_url", window.location.pathname);
          }
          config.body = formData;
        }
        if (this.cart) this.cart.setActiveElement(document.activeElement);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: primaryId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker(
              "add:wait-for-subscribers",
            );
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: primaryId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  "add:wait-for-subscribers",
                  startMarker,
                );
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      "add:paint-updated-sections",
                      () => {
                        this.cart.renderContents(response);
                      },
                    );
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        // Re-query: a variant change swaps the BuyRow innerHTML server-side,
        // which detaches the button cached in the constructor. Without this the
        // toggle would target a stale node and, worse, the enabled-text reset
        // below would clobber the server-rendered "Offerte aanvragen" label of a
        // no-price variant.
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector(
          "span:not(.svg-wrapper)",
        );
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text && this.submitButtonText)
            this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          if (this.submitButtonText)
            this.submitButtonText.textContent =
              this.submitButton.dataset.enabledText ||
              window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    },
  );
}
